import { frameCount, numberOfChannels, type PcmAudio } from "../types";

/**
 * Pick a common sample rate for a set of clips: the highest input rate, so no
 * clip is upsampled beyond its own fidelity loss while all reach a shared rate.
 * Returns 0 for an empty set. Pure.
 */
export function chooseCommonSampleRate(rates: number[]): number {
  return rates.reduce((max, rate) => Math.max(max, rate), 0);
}

/**
 * Concatenate buffers end-to-end into one. Pure.
 *
 * All inputs must share a sample rate (resample first if they don't — that
 * needs the Web Audio adapter). Channel counts are reconciled to the widest
 * input: narrower buffers have their last channel duplicated to fill, so a mono
 * clip joined with a stereo clip plays centered rather than left-only.
 */
export function mergeSequential(clips: PcmAudio[]): PcmAudio {
  if (clips.length === 0) {
    return { sampleRate: 0, channelData: [] };
  }

  const sampleRate = clips[0]?.sampleRate ?? 0;
  for (const clip of clips) {
    if (clip.sampleRate !== sampleRate) {
      throw new Error(
        `mergeSequential: mismatched sample rates (${clip.sampleRate} vs ${sampleRate}); resample before merging`
      );
    }
  }

  const channels = Math.max(...clips.map(numberOfChannels));
  const totalFrames = clips.reduce((sum, clip) => sum + frameCount(clip), 0);

  const channelData = Array.from({ length: channels }, () => new Float32Array(totalFrames));

  let offset = 0;
  for (const clip of clips) {
    const frames = frameCount(clip);
    const clipChannels = numberOfChannels(clip);
    for (let c = 0; c < channels; c++) {
      // Reuse the last available channel when this clip is narrower.
      const source = clip.channelData[Math.min(c, clipChannels - 1)];
      if (source) channelData[c]?.set(source, offset);
    }
    offset += frames;
  }

  return { sampleRate, channelData };
}
